import { AlertTriangle, CheckCircle2, Info } from "lucide-react";
import React, { useMemo } from "react";
import { FORECAST_90_DAY_ENDPOINT } from "@/data/demo-data";
import {
  ANNUAL_TURNOVER,
  BANK_ACCOUNTS,
  MONTHLY_OPERATING_EXPENSES,
  MONTHLY_REVENUE,
} from "@/data/financial-systems";
import { useBaseForecast } from "@/hooks/useBaseForecast";
import { useFormatting } from "@/hooks/useFormatting";
import { useTerminology } from "@/hooks/useTerminology";

export const ExecutiveSummaryPreview: React.FC = () => {
  const { formatCurrency } = useFormatting();
  const { getCashDurationTerm, getCashMovementTerms, getForecastTerm } =
    useTerminology();
  const terms = getCashMovementTerms();
  const forecastTerm = getForecastTerm(false);

  const { baseForecast: forecastData } = useBaseForecast();
  const currentCash = BANK_ACCOUNTS.reduce((sum, a) => sum + a.balance, 0);
  const minBalance = useMemo(
    () => Math.min(...forecastData.map((d) => d.balance)),
    [forecastData],
  );
  const minBalanceDay = useMemo(
    () => forecastData.findIndex((d) => d.balance === minBalance),
    [forecastData, minBalance],
  );

  const kpis = [
    {
      label: "Current Cash Position",
      value: formatCurrency(currentCash),
      sub: "Across all bank accounts",
      status: "neutral" as const,
    },
    {
      label: `90-Day ${forecastTerm.charAt(0).toUpperCase() + forecastTerm.slice(1)} (Base)`,
      value: formatCurrency(FORECAST_90_DAY_ENDPOINT),
      sub: `${formatCurrency(currentCash - FORECAST_90_DAY_ENDPOINT)} projected decline`,
      status: "warning" as const,
    },
    {
      label: "Annual Turnover",
      value: formatCurrency(ANNUAL_TURNOVER),
      sub: "Financial year 2025/26",
      status: "positive" as const,
    },
    {
      label: `Monthly ${terms.in}`,
      value: formatCurrency(MONTHLY_REVENUE),
      sub: "Average — rolling 3 months",
      status: "positive" as const,
    },
    {
      label: "Monthly Operating Costs",
      value: formatCurrency(MONTHLY_OPERATING_EXPENSES),
      sub: "Fixed + variable combined",
      status: "neutral" as const,
    },
    {
      label: getCashDurationTerm(3.9),
      value: "3.9 months",
      sub: "Of operating costs at current rate",
      status: "warning" as const,
    },
    {
      label: "Gross Margin",
      value: "18%",
      sub: "£900K gross profit on £5M revenue",
      status: "positive" as const,
    },
    {
      label: "Net Margin",
      value: "2.8%",
      sub: "£140K net profit — tight but positive",
      status: "warning" as const,
    },
  ];

  const statusIcon = {
    positive: <CheckCircle2 className="h-4 w-4 text-green-600" />,
    warning: <AlertTriangle className="h-4 w-4 text-amber-500" />,
    neutral: <Info className="h-4 w-4 text-muted-foreground" />,
  };

  const highlights = [
    {
      type: "warning" as const,
      heading: "Cash Decline Requires Attention",
      detail: `At the current trajectory, cash is projected to fall from ${formatCurrency(currentCash)} to ${formatCurrency(FORECAST_90_DAY_ENDPOINT)} over 90 days — a decline of ${formatCurrency(currentCash - FORECAST_90_DAY_ENDPOINT)}. The minimum balance of ${formatCurrency(minBalance)} is expected around day ${minBalanceDay}.`,
    },
    {
      type: "positive" as const,
      heading: "Strong Revenue Pipeline",
      detail:
        "Outstanding invoices of £328,500 are expected to be collected over the next 90 days, with BigCo Ltd (£45K), PropertyDev Ltd (£50K) and MegaCorp (£45K) representing the largest receivables.",
    },
    {
      type: "warning" as const,
      heading: "Overdue Receivables — £63,200",
      detail:
        "Three invoices are currently overdue. Recovery of these amounts would materially improve the cash position. MedCo Inc (£34,500) represents the largest overdue balance.",
    },
    {
      type: "positive" as const,
      heading: "Payroll Commitments Well-Managed",
      detail:
        "Biweekly payroll of £45,000 is fully funded for the next 6 cycles. PAYE and NIC obligations are on track with no arrears.",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Company header strip */}
      <div className="rounded-lg border bg-primary/5 border-primary/20 px-6 py-4 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-lg">BuildRight Construction Ltd</h3>
          <p className="text-sm text-muted-foreground">
            Leicester, UK · 50 employees · Financial Year 2025/26
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Report Date</p>
          <p className="font-semibold">
            {new Date().toLocaleDateString("en-GB", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </p>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpis.map((kpi) => (
          <div key={kpi.label} className="rounded-lg border bg-muted/40 p-4">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs text-muted-foreground leading-snug">
                {kpi.label}
              </p>
              {statusIcon[kpi.status]}
            </div>
            <p className="text-lg font-bold mt-2">{kpi.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{kpi.sub}</p>
          </div>
        ))}
      </div>

      {/* Key highlights */}
      <div>
        <p className="text-sm font-semibold mb-3">
          Key Highlights &amp; Commentary
        </p>
        <div className="space-y-3">
          {highlights.map((h, i) => (
            <div
              key={i}
              className={`rounded-lg border px-4 py-3 flex gap-3 ${h.type === "warning" ? "border-amber-200 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-800" : "border-green-200 bg-green-50/60 dark:bg-green-950/20 dark:border-green-800"}`}
            >
              <div className="mt-0.5 flex-shrink-0">
                {h.type === "warning" ? (
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                )}
              </div>
              <div>
                <p
                  className={`text-sm font-semibold ${h.type === "warning" ? "text-amber-800 dark:text-amber-300" : "text-green-800 dark:text-green-300"}`}
                >
                  {h.heading}
                </p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {h.detail}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Prepared by */}
      <div className="rounded-lg border bg-muted/20 px-4 py-3 text-xs text-muted-foreground">
        Prepared by Access Cash Forecaster · Data sourced from Access
        Financials, Paycircle and Excel import · This summary is for internal
        use only
      </div>
    </div>
  );
};
